import React, { useEffect, useState } from 'react'

import cn from 'classnames'
import plusWhite from '../assets/icons/plus-white.svg'
import { useSelector, useDispatch } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import config from '../constants/config'
import ProfileAside from '../components/ProfileAside/ProfileAside'
import Spinner from '../UI/Spinner/Spinner'
import { profilePaths } from '../constants/constans'
import useAdminQuery from '../hooks/useAdminQuery'
import useIndicateBrowserTab from '../hooks/useIndicateBrowserTab'
import { getProfileRoutes } from '../constants/routesConfig'
import PopupAboutSocialMedia from '../components/PopupAboutSocialMedia/PopupAboutSocialMedia'
import MetaTags from '../components/MetaTags/MetaTags'
import { ReactComponent as BankCardsIcon } from '../assets/icons/bank-cards.svg'
import { ReactComponent as TrashIcon } from '../assets/icons/trash-bank-card.svg'

import { setSurveyStatusAction } from '../redux/ui/ui.action'
import StatisticModal from '../components/StatisticModal/StatisticModal'
import MobileBottomBar from '../components/MobileBottomBar/MobileBottomBar'
import useNavigationProfile from '../components/ProfileNav/useNavigationProfile'
import useKeyboard from '../hooks/useKeyboard'
import useScrollDirection from '../hooks/useScrollDirection'
import { setBottomBarVisibleAction } from '../redux/ui/ui.action'

const Profile = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [billingPaymentFormState, setBillingPaymentFormState] = useState({ open: false, hasCard: false })
  const [isUnsubscribeFormOpen, setIsUnsubscribeFormOpen] = useState(false)
  const userName = useSelector(({ user }) => user.profile.first_name)
  const isSettingsRefresh = useSelector(({ user }) => user.settings.is_refresh)
  const { isAuth, admin } = useSelector(({ authentication }) => authentication)
  const hasSurvey = useSelector(({ ui }) => ui.hasSurvey)
  const { navigation } = useNavigationProfile()
  const { isKeyboardOpen } = useKeyboard()
  const scrollDirection = useScrollDirection({
    thresholdDown: 60,
    thresholdUp: 120,
    resetKey: location.pathname,
  })
  const isBottomBarVisible = !isKeyboardOpen && scrollDirection !== 'down'

  useEffect(() => {
    dispatch(setBottomBarVisibleAction(isBottomBarVisible))
  }, [dispatch, isBottomBarVisible])

  const mobileTopbarAction = (() => {
    const pathname = location.pathname
    const actionsByPath = {
      [profilePaths.education]: {
        eventName: 'nextship:education:add',
        ariaLabel: 'Add education',
        imgAlt: 'Add education',
      },
      [profilePaths.experience]: {
        eventName: 'nextship:experience:add',
        ariaLabel: 'Add experience',
        imgAlt: 'Add experience',
      },
      [profilePaths.passports]: {
        eventName: 'nextship:passports:add',
        ariaLabel: 'Add passports',
        imgAlt: 'Add passports',
      },
      [profilePaths.visas]: {
        eventName: 'nextship:visas:add',
        ariaLabel: 'Add visas',
        imgAlt: 'Add visas',
      },
      [profilePaths.licenses]: {
        eventName: 'nextship:licenses:add',
        ariaLabel: 'Add licenses',
        imgAlt: 'Add licenses',
      },
      [profilePaths.certificates]: {
        eventName: 'nextship:certificates:add',
        ariaLabel: 'Add certificates',
        imgAlt: 'Add certificates',
      },
    }

    return actionsByPath[pathname] || null
  })()

  const mobileTopbarTitle = (() => {
    const pathname = location.pathname

    for (const key of Object.keys(navigation)) {
      const item = navigation[key]
      if (item?.paths?.includes(pathname) || key === pathname) return item.title
    }

    if (pathname.startsWith(profilePaths.chat)) return navigation?.[profilePaths.chat]?.title || 'Chats'

    return 'Profile'
  })()

  const handleCloseSurveyModal = () => {
    dispatch(setSurveyStatusAction(false))
  }

  useAdminQuery()
  useIndicateBrowserTab(userName, 'NextShip job at sea for seafarers')

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail
      if (!detail) return
      setBillingPaymentFormState({
        open: Boolean(detail.open),
        hasCard: Boolean(detail.hasCard),
      })
    }

    window.addEventListener('nextship:billing:payment-form-state', handler)
    return () => window.removeEventListener('nextship:billing:payment-form-state', handler)
  }, [])

  useEffect(() => {
    const handler = (e) => {
      setIsUnsubscribeFormOpen(Boolean(e?.detail?.open))
    }
    window.addEventListener('nextship:billing:unsubscribe-form-state', handler)
    return () => window.removeEventListener('nextship:billing:unsubscribe-form-state', handler)
  }, [])

  if (!isAuth) {
    const query = new URLSearchParams(window.location.search)
    const referralLink = query.get('referral')

    if (referralLink) localStorage.setItem('referral', referralLink)

    window.location.href = referralLink
      ? `${config.ROOT_URL}/auth/signup?referral=${referralLink}`
      : `${config.ROOT_URL}/auth/signin`
    return null
  }

  // const isDevEnvironment = () => window.location.href.includes('dev') || window.location.href.includes('localhost')

  // if (isMobileDevice()) return <Redirect to='/' />

  const routes = Object.keys(getProfileRoutes())

  return (
    <>
      <MetaTags
        title='NextShip job at sea for seafarers'
        keywords='ship for sale, cruise ship for sale, cargo ship for sale, tall ship for sale, navy ship for sale, airship for sale, big ship for sale, barge ship for sale, why, for sale, resume examples, examples, air crew member, air crew members, air crew members salary, urban air crew member, air crew member job description, army air crew member, wizz air crew member, urban air crew member pay, air force air crew member, work of air crew member, air crew member jobs, air crew jobs, air crew member salary, find plane crew, plane captain jobs, plane captain job description, plane captain salary, civilian plane captain jobs, plane captains, captain airplane, airline pilot jobs brisbane, boeing captain salary, airline pilot jobs canada, airline pilot jobs california, air pilot jobs canada, float plane pilot jobs canada, captain pilot jobs, airline captain job description resume, airline pilot jobs dubai, airline pilot jobs delta, navy plane captain job description, plane captain salary in india, airline pilot jobs europe, aircraft captain salary, airline captain jobs, airline pilot jobs florida, airline pilot jobs first officer, air pilot government jobs, airline pilot jobs in india, airline pilot jobs in canada, airline pilot jobs in uk, jet captain, airline pilot jobs london, airline pilot job life, plane captain marine corps, airline pilot jobs near me, airplane pilot jobs near me, aircraft pilot jobs near me, plane captain navy, navy plane captain salary, airplane captain oveur, airline pilot related jobs, airline pilot jobs salary, airline pilot jobs south africa, airline pilot jobs seattle, airline pilot jobs sydney, aircraft pilot jobs salary, airline pilot side jobs, airline pilot second jobs, airline pilot similar jobs, what kind of plane does sky captain fly, airline pilot jobs texas, airline pilot jobs usa, captain 777 salary, aircraft captain jobs, aircraft captain vacancy, airline captain jobs, airline captain vacancy, cabin captain, air steward, aircraft steward, air stewardess, aircraft stewardess, airline steward, airline stewardess, cabin steward, cabin stewardess, military navy, military officer, military engineer, military crew member, military mate, maritime forum, global maritime forum, american maritime forum, world maritime forum, malta maritime forum, mediterranean maritime forum, mermaid maritime forum, asean maritime forum, hamburg maritime forum 2024, global maritime forum 2024, global maritime forum 2023, marine fm antenna, global maritime forum annual summit 2023, global maritime forum athens, global maritime forum all aboard alliance, global maritime forum ammonia, naval academy forum, forum shipping agency, marine aquarium forum, marine aquarium forum uk, marine audio forum, asean maritime forum 2023, expanded asean maritime forum, maritime blog, black sea maritime forum, maritime ceo forum singapore, maritime ceo forum, global maritime forum ceo, marine engine forum, european maritime forum, global maritime forum members, sailor forum, trailer sailor forum, cat sailor forum, virtual sailor forum, sailor moon forum, sailor moon cosmos forum, sailor moon crystal forum, virtual sailor ng forum, sailing anarchy forum, sailor forum palermo, sailing forum australia, sailor dress code, sailor age limit, sailboat forum cruisers, sailing boat forum, sailing leadership forum, sailor navy quotes, pilots forums airline, pilot forum usa, sailing forum websites, shooting crew, sailor moon, sailor moon characters, sailor moon umbrella, sailor moon creator, sailor moon author, all sailor moon characters, anime sailor moon, sailor moon cosplay, sailor moon costume, maritime academy, anglo eastern maritime academy, maritime academy address, maritime academy age limit, maritime academy admission portal, maritime academy app, maritime academy admissions, anglo eastern maritime academy mumbai, anglo-eastern maritime academy admission 2023, maritime academy charter school, maritime academy charter school reviews, maritime academy cost, maritime academy careers, maritime academy east coast, maritime academy europe, maritime academy england, maritime academy engineering, maritime academy employment, egypt maritime academy, eastern maritime academy, federal maritime academy, french maritime academy, maritime academy graduate program, maritime academy graduation, maritime academy greece, fishing boat, fishing boat vacancy, fishing boat jobs, fishing ship, fishing ship vacancy, fishing ship jobs, fishing shipping, fishing vessel, pleasure crafts, pleasure ships, pleasure ships vacancies, pleasure ships jobs'
      />
      {/* Mobile fixed topbar (visible only under 1024px by CSS) */}
      <div className='mobile-topbar'>
        <span className='mobile-topbar__title'>{mobileTopbarTitle}</span>
        {location.pathname === profilePaths.billing_services && !billingPaymentFormState.open && (
          <button
            type='button'
            className={cn('mobile-topbar__action', {
              'mobile-topbar__action--disabled': isUnsubscribeFormOpen,
            })}
            onClick={() =>
              !isUnsubscribeFormOpen && window.dispatchEvent(new CustomEvent('nextship:billing:open-payment-form'))
            }
            aria-label='Open payment form'
            disabled={isUnsubscribeFormOpen}
          >
            <BankCardsIcon />
          </button>
        )}

        {location.pathname === profilePaths.billing_services &&
          billingPaymentFormState.open &&
          billingPaymentFormState.hasCard && (
            <button
              type='button'
              className={cn('mobile-topbar__action', 'mobile-topbar__action--detach-card', {
                'mobile-topbar__action--disabled': isUnsubscribeFormOpen,
              })}
              onClick={() =>
                !isUnsubscribeFormOpen && window.dispatchEvent(new CustomEvent('nextship:billing:detach-card'))
              }
              aria-label='Detach card'
              disabled={isUnsubscribeFormOpen}
            >
              <TrashIcon />
            </button>
          )}
        {mobileTopbarAction && (
          <button
            type='button'
            className='mobile-topbar__action'
            onClick={() => window.dispatchEvent(new CustomEvent(mobileTopbarAction.eventName))}
            aria-label={mobileTopbarAction.ariaLabel}
          >
            <img src={plusWhite} alt={mobileTopbarAction.imgAlt} />
          </button>
        )}
      </div>
      <div className={cn('profile-page', { 'profile-page__is-admin': admin?.isAdmin })}>
        <ProfileAside />

        <div className={cn('page', { admin_active_border_right: admin?.isAdmin })}>
          <PopupAboutSocialMedia />

          {hasSurvey && <StatisticModal onClose={handleCloseSurveyModal} />}

          {isSettingsRefresh && (
            <Switch>
              {routes.map((path) => {
                const { exact, Component } = getProfileRoutes()[path]

                return <Route key={path} exact={exact} path={path} component={Component} />
              })}

              <Redirect to={profilePaths.general} />
            </Switch>
          )}

          {!isSettingsRefresh && <Spinner size={100} />}

          {/* spacer to avoid content being hidden behind mobile bottom bar */}
          <div
            className={cn('mobile-bottom-spacer', {
              'mobile-bottom-spacer--hidden': !isBottomBarVisible,
            })}
          />
        </div>
      </div>

      {/* mobile fixed bottom bar */}
      <MobileBottomBar isVisible={isBottomBarVisible} />
    </>
  )
}

export default Profile
